import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { api, unwrap } from '../../api/client';
import type { PlatformAdmin, PlatformRole } from '../../api/m19';
import { PasswordField } from '../../auth/PasswordField';
import { ROLE_LABELS } from '../../auth/capabilities';
import { useNotify } from '../../lib/notify';
import { applyFieldErrors, errorCode } from './formErrors';
import { adminErrorMessage } from './messages';

export const ROLE_HINTS: Record<PlatformRole, string> = {
  SUPER_ADMIN: 'Everything, including plans, suspensions and admin accounts',
  BILLING: 'Subscriptions, extensions and activation codes',
  SUPPORT: 'Read-only tenants, support access and the audit log',
};
const ROLES = Object.keys(ROLE_LABELS) as PlatformRole[];

/** 0–4 score with a label, for the password hint. */
export function passwordStrength(pw: string): { score: number; label: string } {
  if (!pw) return { score: 0, label: '' };
  const classes = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter((r) => r.test(pw)).length;
  let score = 0;
  if (pw.length >= 12) score += 1;
  if (pw.length >= 16) score += 1;
  if (classes >= 3) score += 1;
  if (classes === 4 || pw.length >= 20) score += 1;
  if (pw.length < 12) score = Math.min(score, 1);
  if (/^(.)\1+$/.test(pw)) score = 0;
  const label = ['Too weak', 'Weak', 'Fair', 'Good', 'Strong'][score] ?? '';
  return { score, label };
}

function StrengthHint({ password }: { password: string }) {
  const { score, label } = passwordStrength(password);
  const color = score >= 3 ? 'success' : score === 2 ? 'warning' : 'error';
  return (
    <Box sx={{ mt: -1 }} aria-live="polite">
      <LinearProgress
        variant="determinate"
        value={password ? (score / 4) * 100 : 0}
        color={password ? color : 'inherit'}
        aria-label="Password strength"
        sx={{ height: 6, borderRadius: 3, bgcolor: 'action.hover' }}
      />
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
        {password
          ? `${label} · ${String(password.length)} characters`
          : '12–128 characters. A long passphrase with mixed characters is best.'}
      </Typography>
    </Box>
  );
}

const createSchema = z.object({
  email: z.string().trim().min(1, 'Enter an email').pipe(z.email('Enter a valid email address')),
  name: z.string().trim().min(1, 'Enter a name').max(200),
  role: z.enum(['SUPER_ADMIN', 'BILLING', 'SUPPORT']),
  password: z.string().min(12, 'At least 12 characters').max(128, 'At most 128 characters'),
});
type CreateValues = z.infer<typeof createSchema>;

function RoleSelect({
  value,
  onChange,
  onBlur,
  disabled,
}: {
  value: PlatformRole;
  onChange: (r: PlatformRole) => void;
  onBlur?: () => void;
  disabled?: boolean;
}) {
  return (
    <TextField
      select
      label="Role"
      value={value}
      disabled={disabled}
      onBlur={onBlur}
      onChange={(e) => onChange(e.target.value as PlatformRole)}
      helperText={ROLE_HINTS[value]}
    >
      {ROLES.map((r) => (
        <MenuItem key={r} value={r}>
          {ROLE_LABELS[r]}
        </MenuItem>
      ))}
    </TextField>
  );
}

export function CreateAdminDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      {open && <CreateAdminForm onClose={onClose} />}
    </Dialog>
  );
}

function CreateAdminForm({ onClose }: { onClose: () => void }) {
  const notify = useNotify();
  const qc = useQueryClient();
  const [formError, setFormError] = useState<string | null>(null);
  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<CreateValues>({
    resolver: zodResolver(createSchema),
    mode: 'onBlur',
    defaultValues: { email: '', name: '', role: 'SUPPORT', password: '' },
  });
  const password = useWatch({ control, name: 'password' });

  const onSubmit = handleSubmit(async (v) => {
    setFormError(null);
    try {
      const admin = await unwrap(
        api.POST('/platform/admins', {
          params: { header: { 'idempotency-key': crypto.randomUUID() } },
          body: {
            email: v.email.trim().toLowerCase(),
            name: v.name.trim(),
            role: v.role,
            password: v.password,
          },
        }),
      );
      await qc.invalidateQueries({ queryKey: ['platform-admins'] });
      notify.success(`Added ${admin.name}. Share the password with them securely.`);
      onClose();
    } catch (e) {
      if (errorCode(e) === 'EMAIL_TAKEN') {
        setError('email', { type: 'server', message: adminErrorMessage(e) });
      } else if (!applyFieldErrors(e, setError, ['email', 'name', 'role', 'password'])) {
        setFormError(adminErrorMessage(e));
      }
    }
  });

  return (
    <form onSubmit={(e) => void onSubmit(e)} noValidate>
      <DialogTitle sx={{ fontWeight: 700 }}>
        Add a platform admin
        <Typography variant="body2" color="text.secondary">
          They sign in to this console with the email and password you set here, and set up two-step
          verification on first sign-in where it is required.
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.25} sx={{ pt: 1 }}>
          {formError && <Alert severity="error">{formError}</Alert>}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Name"
              autoFocus
              fullWidth
              {...register('name')}
              error={Boolean(errors.name)}
              helperText={errors.name?.message}
            />
            <TextField
              label="Work email"
              type="email"
              fullWidth
              autoComplete="off"
              {...register('email')}
              error={Boolean(errors.email)}
              helperText={errors.email?.message}
            />
          </Stack>
          <Controller
            control={control}
            name="role"
            render={({ field }) => (
              <RoleSelect value={field.value} onChange={field.onChange} onBlur={field.onBlur} />
            )}
          />
          <PasswordField
            label="Initial password"
            autoComplete="new-password"
            registration={register('password')}
            error={errors.password?.message}
          />
          <StrengthHint password={password} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button variant="outlined" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" variant="contained" loading={isSubmitting}>
          Add admin
        </Button>
      </DialogActions>
    </form>
  );
}

const editSchema = z.object({
  name: z.string().trim().min(1, 'Enter a name').max(200),
  role: z.enum(['SUPER_ADMIN', 'BILLING', 'SUPPORT']),
});
type EditValues = z.infer<typeof editSchema>;

export function EditAdminDialog({
  admin,
  isSelf,
  onClose,
}: {
  admin: PlatformAdmin | null;
  isSelf: boolean;
  onClose: () => void;
}) {
  return (
    <Dialog open={admin !== null} onClose={onClose} maxWidth="xs" fullWidth>
      {admin && <EditAdminForm admin={admin} isSelf={isSelf} onClose={onClose} />}
    </Dialog>
  );
}

function EditAdminForm({
  admin,
  isSelf,
  onClose,
}: {
  admin: PlatformAdmin;
  isSelf: boolean;
  onClose: () => void;
}) {
  const notify = useNotify();
  const qc = useQueryClient();
  const [formError, setFormError] = useState<string | null>(null);
  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<EditValues>({
    resolver: zodResolver(editSchema),
    mode: 'onBlur',
    defaultValues: { name: admin.name, role: admin.role },
  });

  const onSubmit = handleSubmit(async (v) => {
    setFormError(null);
    try {
      await unwrap(
        api.PATCH('/platform/admins/{id}', {
          params: { path: { id: admin.id } },
          body: {
            ...(v.name.trim() !== admin.name ? { name: v.name.trim() } : {}),
            ...(v.role !== admin.role ? { role: v.role } : {}),
            version: admin.version,
          },
        }),
      );
      await qc.invalidateQueries({ queryKey: ['platform-admins'] });
      notify.success(`Saved ${v.name.trim()}`);
      onClose();
    } catch (e) {
      if (!applyFieldErrors(e, setError, ['name', 'role'])) setFormError(adminErrorMessage(e));
      if (errorCode(e) === 'VERSION_CONFLICT')
        await qc.invalidateQueries({ queryKey: ['platform-admins'] });
    }
  });

  return (
    <form onSubmit={(e) => void onSubmit(e)} noValidate>
      <DialogTitle sx={{ fontWeight: 700 }}>
        Edit {admin.name}
        <Typography variant="body2" color="text.secondary">
          {admin.email}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.25} sx={{ pt: 1 }}>
          {formError && <Alert severity="error">{formError}</Alert>}
          <TextField
            label="Name"
            autoFocus
            {...register('name')}
            error={Boolean(errors.name)}
            helperText={errors.name?.message}
          />
          <Controller
            control={control}
            name="role"
            render={({ field }) => (
              <RoleSelect value={field.value} onChange={field.onChange} onBlur={field.onBlur} />
            )}
          />
          {isSelf && (
            <Alert severity="info" variant="outlined">
              This is your own account. Changing your role takes effect at your next sign-in.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button variant="outlined" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" variant="contained" loading={isSubmitting} disabled={!isDirty}>
          Save changes
        </Button>
      </DialogActions>
    </form>
  );
}
